import '/@front:css/style.css'

import { createApp, h } from 'vue'

import ArtworkLightbox from '/@front:components/ArtworkLightbox.vue'

// Lazy-load Video.js only on pages that have videos
const hasVideoPlayer = document.querySelector('video-player')
const hasVideoArtworks = window.__artworks?.some((a) => a.hasVideo)

if (hasVideoPlayer || hasVideoArtworks) {
  import('./video-player')
}

// Mount artwork lightbox on artist detail pages
const lightboxEl = document.getElementById('artwork-lightbox')

if (lightboxEl) {
  createApp({
    name: 'ArtworkLightboxApp',
    render: () => h(ArtworkLightbox),
  }).mount(lightboxEl)
}

// Submit forms via fetch, show validation errors / success inline
for (const form of document.querySelectorAll<HTMLFormElement>('form[data-async]')) {
  // Lives in the markup rather than being created here, so assistive tech has
  // already registered the live region by the time a message lands in it.
  const status = form.querySelector<HTMLElement>('.form-feedback')

  const announce = (message: string, kind: 'success' | 'error') => {
    if (!status) return

    status.className = `form-feedback ${kind}`
    status.textContent = message
  }

  const clearFeedback = () => {
    form.querySelectorAll('.field-error').forEach((el) => el.remove())
    form.querySelectorAll('[aria-invalid]').forEach((el) => {
      el.removeAttribute('aria-invalid')
      el.removeAttribute('aria-describedby')
    })

    if (status) status.textContent = ''
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    clearFeedback()

    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true

    try {
      const res = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      })

      if (res.status === 419) {
        announce('Uw sessie is verlopen. Ververs de pagina en probeer het opnieuw.', 'error')

        return
      }

      const data = await res.json()

      if (!res.ok && data.errors) {
        let firstInvalid: HTMLElement | null = null

        for (const [field, messages] of Object.entries<string[]>(data.errors)) {
          const input = form.querySelector<HTMLElement>(`[name="${field}"]`)

          if (!input) continue

          const error = document.createElement('p')
          error.className = 'field-error'
          error.id = `${input.id}-error`
          error.textContent = messages[0]

          input.setAttribute('aria-invalid', 'true')
          input.setAttribute('aria-describedby', error.id)
          input.insertAdjacentElement('afterend', error)

          firstInvalid ??= input
        }

        // Focus, not a live-region message: moving to the field announces its
        // label and its error together, and the two would talk over each other.
        firstInvalid?.focus()

        return
      }

      if (res.ok && data.message) {
        form.reset()
        announce(data.message, 'success')
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false
    }
  })
}

// Prefetch full artwork image on hover
const artworksMap = new Map((window.__artworks ?? []).map((a) => [a.sqid, a.image]))

document.querySelectorAll<HTMLElement>('#artwork-grid a[data-sqid]').forEach((el) => {
  el.addEventListener(
    'pointerenter',
    () => {
      const src = artworksMap.get(el.dataset.sqid ?? '')
      if (src) new Image().src = src
    },
    { once: true },
  )
})

// Artwork grid: progressive loading ("show more" button)
const showMoreBtn = document.getElementById('show-all-thumbs')

if (showMoreBtn) {
  const items = document.querySelectorAll('#artwork-grid li')
  const initialCount = 8
  let shown = initialCount

  items.forEach((item, index) => {
    if (index >= initialCount) {
      ;(item as HTMLElement).style.display = 'none'
    }
  })

  // The thumbnails appear one after another; to someone who asked for less
  // motion that is the animation, so reveal them in one go instead.
  const stagger = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 80

  showMoreBtn.addEventListener('click', () => {
    const firstRevealed = items[shown] as HTMLElement | undefined

    const reveal = () => {
      if (shown < items.length) {
        ;(items[shown] as HTMLElement).style.display = ''
        shown++

        if (stagger === 0) {
          reveal()
        } else {
          setTimeout(reveal, stagger)
        }

        return
      }

      // Removing the wrapper destroys the button that has focus, which would
      // drop the keyboard user back to the top of the document. Hand focus to
      // the first artwork that just appeared instead.
      showMoreBtn.parentElement?.remove()
      firstRevealed?.querySelector('a')?.focus()
    }

    reveal()
  })
}
